import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { UnlocksProvider } from "./lib/unlocks";
import Nav from "./components/Nav";
import Catalogue from "./pages/Catalogue";
import AnimeDetail from "./pages/AnimeDetail";
import Stats from "./pages/Stats";
import Recommendations from "./pages/Recommendations";
import Personality from "./pages/Personality";
import Timeline from "./pages/Timeline";
import ImportPage from "./pages/ImportPage";
import Schedule from "./pages/Schedule";
import PersonaShared from "./pages/PersonaShared";
import PersonaHistory from "./pages/PersonaHistory";
import Discover from "./pages/Discover";
import Support from "./pages/Support";
import SupportContact from "./pages/SupportContact";
import Roadmap from "./pages/Roadmap";
import PaymentSuccess, { PaymentCancel } from "./pages/PaymentResult";
import AuthCallback from "./pages/AuthCallback";
import Account from "./pages/Account";
import HandleRedirect, { HandleNotFound } from "./pages/HandleRedirect";
import PublicProfile from "./pages/PublicProfile";
import Wrapped from "./pages/Wrapped";
import Feed from "./pages/Feed";
import Compare from "./pages/Compare";
import PaywallGate from "./components/PaywallGate";
import { Link } from "react-router-dom";

const TOAST_STYLE = {
  background: "#0a0a0a",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const TOAST_OPTIONS = { style: TOAST_STYLE };

function NotFound() {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10 text-center" data-testid="not-found">
      <h1 className="font-display text-6xl uppercase text-white md:text-8xl">404</h1>
      <p className="mt-3 text-neutral-400">This page went off-canon.</p>
      <Link to="/" className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
        Back to catalogue
      </Link>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Detect Google Auth callback synchronously during render to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Catalogue />} />
        <Route path="/anime/:mal_id" element={<AnimeDetail />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/recommendations" element={<Recommendations />} />
        <Route path="/personality" element={<Personality />} />
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/import" element={<PaywallGate feature="Bulk Import" tier="pro_pass"><ImportPage /></PaywallGate>} />
        <Route path="/schedule" element={<PaywallGate feature="Seasonal Schedule" tier="pro_pass"><Schedule /></PaywallGate>} />
        <Route path="/persona/history" element={<PaywallGate feature="Persona History" tier="persona_only"><PersonaHistory /></PaywallGate>} />
        <Route path="/discover" element={<PaywallGate feature="Discover" tier="pro_pass"><Discover /></PaywallGate>} />
        <Route path="/premium" element={<Support />} />
        <Route path="/support" element={<SupportContact />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/account" element={<Account />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/p/:share_id" element={<PersonaShared />} />
        <Route path="/u/:handle" element={<PublicProfile />} />
        <Route path="/wrapped/:year" element={<Wrapped />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/compare/:handleA/:handleB" element={<Compare />} />
        <Route path="/handle-not-found/:handle" element={<HandleNotFound />} />
        <Route path="/not-found" element={<NotFound />} />
        {/* Catch-all: /@handle shortlinks resolve inside HandleRedirect; anything else falls through to /not-found */}
        <Route path="/:atHandle" element={<HandleRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App grain min-h-screen bg-[#050505] text-neutral-100">
      <BrowserRouter>
        <AuthProvider>
          <UnlocksProvider>
            <AppRouter />
          </UnlocksProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

export default App;
